import type { SectionKey } from './types';

export interface SectionDef {
  key: SectionKey;
  /** Letter heading (italic serif on the letter page) */
  heading: string;
  /** Form-side legend (matches heading but lowercase prose) */
  formLegend: string;
  /** Example prompts the user can read to find a way in */
  examples: string[];
  /** Starter phrases the user can copy-adapt */
  starters: string[];
  /** Placeholder in the preview when the section is empty */
  placeholder: string;
}

export const SECTIONS: SectionDef[] = [
  {
    key: 'intent',
    heading: 'intent',
    formLegend: 'intent',
    examples: [
      'what do you hope to explore, build, or deepen?',
      'what brought you to writing this letter?',
      'what would success feel like a year from now?',
    ],
    starters: [
      'i am writing this because…',
      'what i want to explore is…',
      'what draws me to you, specifically, is…',
    ],
    placeholder: '[your intent goes here]',
  },
  {
    key: 'context',
    heading: 'context',
    formLegend: 'context',
    examples: [
      'who are you right now? experience, life situation, current state.',
      'what should your reader know about where you are coming from?',
    ],
    starters: [
      'i am at a place in my life where…',
      'what i bring is…',
      'a thing you should know about me is…',
    ],
    placeholder: '[your context goes here]',
  },
  {
    key: 'yesPlease',
    heading: 'yes, please',
    formLegend: 'yes, please',
    examples: [
      'what desires do you want more of?',
      'what makes you feel most yourself?',
    ],
    starters: [
      'i want…',
      'i light up when…',
      'what i would never get tired of is…',
    ],
    placeholder: '[your yes-please list goes here]',
  },
  {
    key: 'curiosities',
    heading: 'curiosities',
    formLegend: 'curiosities',
    examples: [
      'what interests you that you have not yet committed to?',
      'where would you want to be invited, gently, to try?',
    ],
    starters: [
      'i am curious about…',
      'i would like to try, with the right person…',
      'something i have wondered about is…',
    ],
    placeholder: '[your curiosities go here]',
  },
  {
    key: 'softLimits',
    heading: 'soft limits',
    formLegend: 'soft limits',
    examples: [
      'what possibilities exist under specific conditions or with the right person?',
      'what would you approach only with care?',
    ],
    starters: [
      'i may, under these conditions…',
      'i would approach this only if…',
    ],
    placeholder: '[your soft limits go here]',
  },
  {
    key: 'hardLimits',
    heading: 'hard limits',
    formLegend: 'hard limits',
    examples: [
      'what activities, situations, or dynamics are non-negotiable nos?',
      'what experiences from your past should never be repeated?',
    ],
    starters: [
      'i do not consent to…',
      'i will not participate in…',
      'this is not a list of suggestions. these are nos.',
    ],
    placeholder: '[your hard limits go here]',
  },
  {
    key: 'safewords',
    heading: 'safewords',
    formLegend: 'safewords',
    examples: [
      'what signals do you propose, and what do they mean?',
      'traffic-light system, custom words, or non-verbal cues?',
    ],
    starters: [
      'my safewords are: red means stop everything; yellow means slow down or check in.',
      'if i cannot speak, my signal is…',
    ],
    placeholder: '[your safewords go here]',
  },
  {
    key: 'aftercare',
    heading: 'aftercare',
    formLegend: 'aftercare',
    examples: [
      'what do you need after intensity?',
      'physical, emotional, immediate, next-day?',
    ],
    starters: [
      'after a heavy scene, i need…',
      'in the first hour, please…',
      'in the days after, i may experience…',
    ],
    placeholder: '[your aftercare needs go here]',
  },
  {
    key: 'lifeAround',
    heading: 'the life around it',
    formLegend: 'the life around it',
    examples: [
      'what stays outside the dynamic?',
      'who else matters and how do they fit?',
    ],
    starters: [
      'these things stay outside the dynamic…',
      "what i need from my non-dynamic life is…",
      'others who matter to me are…',
    ],
    placeholder: '[what stays outside the dynamic goes here]',
  },
  {
    key: 'exitTerms',
    heading: 'exit terms',
    formLegend: 'exit terms',
    examples: [
      'how does either of us end the dynamic with grace?',
      'what would a kind ending look like?',
    ],
    starters: [
      'if either of us needs to end this, i propose…',
      'the way i want to be told something is not working is…',
    ],
    placeholder: '[your exit terms go here]',
  },
  {
    key: 'signature',
    heading: 'signature',
    formLegend: 'closing & signature',
    examples: [
      'how do you want to sign off? a name, a title, a closing phrase.',
    ],
    starters: [
      'with care,',
      'yours, with intention,',
      'signed, [your name], [today\'s date]',
    ],
    placeholder: '[your signature goes here]',
  },
];
